// Issues list hook — fetch, filter, sort, paginate issues
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from './useAuth'
import type { IssueStatus } from '@/types/database'

interface UseIssuesOptions {
  status?: IssueStatus
  customerName?: string
  industry?: string
  d365Version?: string
  sortField?: string
  sortDirection?: 'asc' | 'desc'
  page?: number
  pageSize?: number
}

export interface IssueRow {
  id: string
  title: string
  status: string
  priority: string
  category: string | null
  d365_version: string
  d365_module: string | null
  customer_name: string | null
  customer_email: string | null
  industry: string | null
  created_at: string
  assigned_to: string | null
  created_by: string
}

interface IssueStats {
  total: number
  open: number
  in_progress: number
  resolved: number
}

const ISSUES_KEY = 'issues'
const SELECT_COLS = 'id, title, status, priority, category, d365_version, d365_module, customer_name, customer_email, industry, created_at, assigned_to, created_by'

export function useIssues(options: UseIssuesOptions = {}) {
  const { user } = useAuth()
  const {
    status, customerName, industry, d365Version,
    sortField = 'created_at', sortDirection = 'desc',
    page = 0, pageSize = 20,
  } = options

  const offset = page * pageSize

  const issuesQuery = useQuery({
    queryKey: [ISSUES_KEY, status, customerName, industry, d365Version, sortField, sortDirection, page, pageSize],
    queryFn: async (): Promise<{ rows: IssueRow[]; total: number }> => {
      let query = supabase
        .from('issues')
        .select(SELECT_COLS, { count: 'exact' })
        .order(sortField, { ascending: sortDirection === 'asc' })
        .range(offset, offset + pageSize - 1)

      if (status) query = query.eq('status', status)
      if (customerName) query = query.ilike('customer_name', `%${customerName}%`)
      if (industry) query = query.eq('industry', industry)
      if (d365Version) query = query.eq('d365_version', d365Version)

      const { data, error, count } = await query
      if (error) throw error
      return { rows: (data ?? []) as unknown as IssueRow[], total: count ?? 0 }
    },
    enabled: !!user,
    staleTime: 30_000,
    retry: 1,
  })

  const filtersQuery = useQuery({
    queryKey: [ISSUES_KEY, 'filters'],
    queryFn: async () => {
      const results = await Promise.allSettled([
        supabase.from('issues').select('customer_name').not('customer_name', 'is', null).order('customer_name'),
        supabase.from('issues').select('industry').not('industry', 'is', null).order('industry'),
        supabase.from('issues').select('d365_version').order('d365_version'),
      ])

      const extract = (result: PromiseSettledResult<{ data: Record<string, unknown>[] | null }>, key: string): string[] => {
        if (result.status !== 'fulfilled' || !result.value.data) return []
        return [...new Set(result.value.data.map((r) => String(r[key])).filter(Boolean))]
      }

      return {
        customers: extract(results[0], 'customer_name'),
        industries: extract(results[1], 'industry'),
        versions: extract(results[2], 'd365_version'),
      }
    },
    enabled: !!user,
    staleTime: 60_000,
  })

  const statsQuery = useQuery({
    queryKey: [ISSUES_KEY, 'stats'],
    queryFn: async (): Promise<IssueStats> => {
      const [total, open, inProgress, resolved] = await Promise.all([
        supabase.from('issues').select('id', { count: 'exact', head: true }),
        supabase.from('issues').select('id', { count: 'exact', head: true }).eq('status', 'open'),
        supabase.from('issues').select('id', { count: 'exact', head: true }).eq('status', 'in_progress'),
        supabase.from('issues').select('id', { count: 'exact', head: true }).eq('status', 'resolved'),
      ])
      return {
        total: total.count ?? 0, open: open.count ?? 0,
        in_progress: inProgress.count ?? 0, resolved: resolved.count ?? 0,
      }
    },
    enabled: !!user,
    staleTime: 30_000,
  })

  return {
    issues: issuesQuery.data?.rows ?? [],
    totalCount: issuesQuery.data?.total ?? 0,
    stats: statsQuery.data ?? { total: 0, open: 0, in_progress: 0, resolved: 0 },
    filterOptions: filtersQuery.data ?? { customers: [], industries: [], versions: [] },
    isLoading: issuesQuery.isLoading,
    isStatsLoading: statsQuery.isLoading,
    error: issuesQuery.error,
    refetch: issuesQuery.refetch,
  }
}
